import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Avatar, Btn, Card, ProcessStepper, RoleChip, Skeleton, SkeletonText, useToast } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import {
  GAM_REFRESH_TARGET,
  RESELLER_ALLOWED_REFRESH_TARGET,
  latestEventId,
  latestGamSyncEvent,
  useGamSyncProgress,
  useGrantProcess,
  useRequest,
} from '@/hooks';
import { approveRequest, rejectRequest, retrySyncRequest } from '@/api/requests';
import type {
  AfPriceBatchOperation,
  AfPriceChangeInput,
  AfPriceCurrency,
  Level,
  RequestRecord,
  RequestType,
} from '@/api';
import { getErrorMessage, isNotFound } from '@/api';
import { useMetaContext } from '@/components/Layout';
import {
  buildGamesById,
  emailLocal,
  formatTimestamp,
  gameLabel,
  networkMetaKindLabel,
  requestTypeLabel,
  resellerAllowedKindLabel,
  stageGrantChanges,
  stageName,
} from '@/lib/format';
import ProjectRequestCards from './projectDetail/ProjectRequestCards';
import { formatInputAndApplied, formatVatIncluded, priceOperationAction } from './prices/afCpeShared';
import PriceDeleteNotice from './prices/PriceDeleteNotice';

import type { RefreshPolicy } from '@/api/networkMeta';
import type { ResellerAllowedRequestPayload, ResellerAllowedRow } from '@/api/resellerAllowed';

import { POLICY_LABEL, policyKey } from './networkMeta/networkMetaFormat';

import styles from './RequestDetail.module.css';

// 끝 이벤트 없이 이 시간이 지나면 중단된 실행으로 본다(서버 잠금 대기·run 대기 상한보다 넉넉히).
const GAM_SYNC_STALE_MS = 70 * 60 * 1000;

export default function RequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const meta = useMetaContext();
  const isAdmin = meta.current_user.platform_role === 'admin';

  const id = Number(requestId);
  const requestState = useRequest(Number.isFinite(id) ? id : undefined);
  const [busy, setBusy] = useState(false);
  // 승인 후속처리(BQ ACL · access_grants · redash · partners refresh)를 직접 부여와 동일한 스텝퍼로 표시.
  const { processSteps, processTitle, runGrantProcess, closeStepper } = useGrantProcess();
  // Admon 네트워크 승인 — 메타데이터 반영 뒤 GAM 매핑 동기화(Dagster)까지 단계별로 보여 준다.
  // 리셀러 허용 승인은 같은 추적에 대상만 바꿔 정본 반영 뒤 reseller_allowed 재생성까지 보여 준다.
  // 대상은 저장하지 않고 부를 때 읽으므로 요청이 도착한 뒤 렌더에서 골라도 된다.
  const resellerAllowed = requestState.data?.request_type === 'reseller_allowed_apply';
  const gamSync = useGamSyncProgress(
    () => requestState.reload(),
    resellerAllowed ? RESELLER_ALLOWED_REFRESH_TARGET : GAM_REFRESH_TARGET,
  );

  // 재조회(승인·추적 중 reload) 때는 기존 화면을 유지한다 — 진행 팝업이 깜빡이지 않게.
  if (requestState.loading && !requestState.data) {
    // 요청 상세 로딩 — 카드 골격 스켈레톤. (규칙: orbit-loading-skeleton 스킬)
    return (
      <div className={styles.page}>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Skeleton width="40%" height={20} radius={6} />
            <SkeletonText lines={3} lastWidth="55%" />
            <Skeleton width="100%" height={1} />
            <SkeletonText lines={2} lastWidth="65%" />
          </div>
        </Card>
      </div>
    );
  }

  if (requestState.error || !requestState.data) {
    const notFound = requestState.error ? isNotFound(requestState.error) : false;
    return (
      <div className={styles.page}>
        <Card>
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ font: '700 16px/1.3 "Pretendard"', marginBottom: 12 }}>
              {notFound ? '요청을 찾을 수 없어요' : '요청을 불러오지 못했어요'}
            </div>
            {requestState.error && (
              <div style={{ color: 'var(--sc-text-muted)', marginBottom: 16 }}>
                {getErrorMessage(requestState.error)}
              </div>
            )}
            <Link to="/inbox" style={{ textDecoration: 'none' }}>
              <Btn variant="primary">승인 대기로</Btn>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const r = requestState.data;

  // GAM 코드를 바꿀 수 있는 건 네트워크 저장(save)뿐이다 — 판정 그룹(policy)·추가(create)는
  // GAM 동기화가 돌지 않으므로 기존 토스트 흐름으로 처리한다. 리셀러 허용은 반영되면 언제나
  // 재생성을 건다(추가·변경·삭제 모두).
  const gamTracked = (r.request_type === 'network_meta_apply' && r.payload?.kind === 'save') || resellerAllowed;

  const approve = async () => {
    if (gamTracked) {
      setBusy(true);
      try {
        const [title, doneText] = resellerAllowed
          ? ['리셀러 허용 반영 승인 중', '리셀러 허용 반영 요청을 승인했어요']
          : ['Admon 네트워크 반영 승인 중', 'Admon 네트워크 반영 요청을 승인했어요'];
        await gamSync.runApply(title, () => approveRequest(r.id), latestEventId(r), () =>
          toast.success(doneText),
        );
      } finally {
        setBusy(false);
      }
      return;
    }
    if (r.request_type === 'price_apply' || r.request_type === 'network_meta_apply' || r.request_type === 'stage_grant'
        || r.request_type === 'project_edit' || r.request_type === 'project_create') {
      // 반영 결과가 approve 응답의 bq_sync_status 로 오는 타입 — 실패해도 예외가 아니라 상태로 알려준다.
      const [failText, doneText] = {
        price_apply: ['단가 반영에 실패했어요', 'AF CPE 단가 반영 요청을 승인했어요'],
        network_meta_apply: ['Admon 네트워크 반영에 실패했어요', 'Admon 네트워크 반영 요청을 승인했어요'],
        stage_grant: ['Stage 권한 반영에 실패했어요', 'Stage 권한 요청을 승인하고 반영했어요'],
        project_edit: ['앱 정보 반영에 실패했어요', '앱 정보 수정 요청을 승인하고 반영했어요'],
        project_create: ['앱 등록에 실패했어요', '앱 등록 요청을 승인하고 반영했어요'],
      }[r.request_type];
      setBusy(true);
      try {
        const approved = await approveRequest(r.id);
        if (approved.bq_sync_status === 'failed') {
          toast.error(approved.bq_sync_error ?? failText);
        } else {
          toast.success(doneText);
        }
        requestState.reload();
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      // 직접 부여와 동일한 후속처리(BQ ACL patch · access_grants · redash · partners refresh)가
      // approve_request → _run_bq_sync에서 진행되므로, 진행 과정을 같은 스텝퍼로 보여준다.
      const ok = await runGrantProcess(
        `${emailLocal(r.target_email ?? r.requester_email)}님 ${displayRequestTypeLabel(r.request_type)} 승인 중`,
        [() => approveRequest(r.id).then(() => ({}))],
        () => requestState.reload(),
      );
      if (!ok) return;
    } catch (err) {
      closeStepper();
      toast.error(getErrorMessage(err));
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await rejectRequest(r.id);
      toast.info('요청을 반려했어요');
      setTimeout(() => navigate('/inbox'), 400);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (gamTracked) {
      setBusy(true);
      try {
        await gamSync.runApply(
          resellerAllowed ? '허용 목록 반영 재시도 중' : '메타데이터 반영 재시도 중',
          () => retrySyncRequest(r.id),
          latestEventId(r),
        );
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      const retried = await retrySyncRequest(r.id);
      if (retried.bq_sync_status === 'failed') {
        toast.error(retried.bq_sync_error ?? '반영 재시도에 실패했어요');
      } else if (retried.bq_sync_status === 'pending') {
        toast.info('반영 상태를 다시 확인해 주세요');
      } else {
        toast.success('BQ 싱크를 재시도했어요');
      }
      requestState.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // 리셀러 허용은 이벤트 접두만 다르다(reseller_allowed_refresh_*). 'reseller_refresh_' 로 시작하지 않아 섞이지 않는다.
  const syncPrefix = resellerAllowed ? RESELLER_ALLOWED_REFRESH_TARGET.eventPrefix : GAM_REFRESH_TARGET.eventPrefix;
  const gamSyncEvent = r.request_type === 'network_meta_apply' || resellerAllowed
    ? latestGamSyncEvent(r, syncPrefix)
    : undefined;
  const gamApplied = r.status === 'approved' && r.bq_sync_status === 'done';
  const gamSyncFailed = gamSyncEvent?.event_type === `${syncPrefix}failed` && gamApplied;
  // 대기·실행 중으로 남은 채 추적 상한을 넘긴 실행 — 인스턴스 재시작 등으로 끝 이벤트가 빠졌을 수 있다.
  const gamSyncStale =
    gamApplied
    && (gamSyncEvent?.event_type === `${syncPrefix}queued` || gamSyncEvent?.event_type === `${syncPrefix}started`)
    && Date.now() - new Date(gamSyncEvent.created_at).getTime() > GAM_SYNC_STALE_MS;

  const retryGamSync = async () => {
    setBusy(true);
    try {
      await gamSync.runRetry(r.id);
    } finally {
      setBusy(false);
    }
  };

  const statusClass =
    r.status === 'approved' ? styles.approved : r.status === 'rejected' ? styles.rejected : '';

  return (
    <div className={styles.page}>
      {/* 헤더 */}
      <div className={styles.card}>
        <div className={styles.headerCard}>
          <div className={styles.typeIcon}>
            <NavIcon name="inbox" size={28} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 className={styles.title}>{displayRequestTypeLabel(r.request_type)}</h1>
              <span className={`${styles.statusChip} ${statusClass}`}>
                <span className={styles.statusDot} />
                {r.status === 'pending' ? '검토 대기' : r.status === 'approved' ? '승인됨' : '반려됨'}
              </span>
            </div>
            <div className={styles.meta}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Avatar email={r.requester_email} name={emailLocal(r.requester_email)} size={20} />
                {emailLocal(r.requester_email)}
              </span>
              <span className={styles.metaDot} />
              <span>{formatTimestamp(r.requested_at)}</span>
              {r.prefix && (
                <>
                  <span className={styles.metaDot} />
                  <Link
                    to={`/projects/${r.prefix}`}
                    style={{ color: 'var(--sc-text)', fontWeight: 600 }}
                  >
                    {r.prefix}
                  </Link>
                </>
              )}
              <span className={styles.metaDot} />
              <span>
                요청 ID <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', color: 'var(--sc-text)' }}>{r.id}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 삭제(off)가 섞인 단가 요청은 승인 전에 먼저 보이도록 맨 위에 안내한다. */}
      {r.request_type === 'price_apply' && (
        <PriceDeleteNotice count={priceOperations(r).filter((operation) => priceOperationAction(operation) === 'delete').length} />
      )}

      {/* Payload */}
      {r.request_type === 'project_create' || r.request_type === 'project_edit' ? (
        // 프로젝트 요청은 입력 화면과 동일한 카드(CommonInfoCard/PlatformInfoCard)를 읽기전용으로 재활용.
        <div>
          <div className={styles.section} style={{ padding: '0 4px' }}>요청 내용</div>
          <ProjectRequestCards requestType={r.request_type} payload={r.payload} />
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.section}>요청 내용</div>
          <RequestPayload record={r} />
        </div>
      )}

      {/* Reason */}
      {r.reason && (
        <div className={styles.card}>
          <div className={styles.section}>요청 사유</div>
          <div className={styles.reasonBox}>{r.reason}</div>
        </div>
      )}

      {/* Events */}
      {r.events.length > 0 && (
        <div className={styles.card}>
          <div className={styles.section}>이벤트 로그</div>
          <div className={styles.eventList}>
            {r.events.map((ev) => (
              <div key={ev.id} className={styles.eventRow}>
                <span className={styles.eventType}>{ev.event_type}</span>
                <span style={{ color: 'var(--sc-text-muted)' }}>
                  {ev.actor_email ? emailLocal(ev.actor_email) : 'system'}
                </span>
                <span style={{ color: 'var(--sc-ink-5, #5C5C5C)' }}>{ev.note || ''}</span>
                <span style={{ color: 'var(--sc-text-muted)', whiteSpace: 'nowrap' }}>
                  {formatTimestamp(ev.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Decision bar */}
      {r.status === 'pending' && isAdmin && (
        <div className={styles.decisionBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, font: '500 12px/1.5 "Pretendard"', color: 'var(--sc-ink-5, #5c5c5c)' }}>
            <Avatar email={meta.current_user.email} name={emailLocal(meta.current_user.email)} size={28} />
            <div>
              <div style={{ font: '600 13px/1.2 "Pretendard"', color: 'var(--sc-text)' }}>
                {emailLocal(meta.current_user.email)} (관리자)
              </div>
              <div>
                검토 후 결정해주세요. 승인 시
                {r.request_type === 'price_apply' ? ' AF CPE 단가에' : ''}
            {r.request_type === 'network_meta_apply' ? ' Admon 네트워크에' : ''}
            {resellerAllowed ? ' 리셀러 허용 목록에' : ''} 즉시 적용돼요.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="default" onClick={() => navigate('/inbox')}>
              뒤로
            </Btn>
            <Btn variant="danger" onClick={reject} disabled={busy || processSteps !== null}>
              반려
            </Btn>
            <Btn variant="dark" onClick={approve} disabled={busy || processSteps !== null}>
              승인
            </Btn>
          </div>
        </div>
      )}

      {/* 앱 수정/network_meta는 승인과 반영이 두 단계라, 승인 커밋과
          반영 사이에 서버가 죽으면 'approved + pending' 으로 멈춘다 — 반영은 동기라
          상세를 읽는 시점의 pending 은 진행 중이 아니라 중단이다. failed 만 보면 이
          상태가 정상 승인처럼 보여 복구 경로가 없다(서버 재시도는 pending 도 받는다). */}
      {isAdmin
        && (r.bq_sync_status === 'failed'
          || (r.bq_sync_status === 'pending'
            && ['network_meta_apply', 'reseller_allowed_apply', 'project_edit', 'project_create'].includes(r.request_type))) && (
        <div className={styles.card} style={{ borderColor: '#FFD8D2', background: '#FFF6F4' }}>
          <div className={styles.section} style={{ color: '#A12D14' }}>
            {r.request_type === 'price_apply'
              ? '단가 반영 실패'
              : r.request_type === 'network_meta_apply'
                ? r.bq_sync_status === 'pending'
                  ? '메타데이터 반영이 중단됨'
                  : '메타데이터 반영 실패'
                : resellerAllowed
                  ? r.bq_sync_status === 'pending'
                    ? '리셀러 허용 반영이 중단됨'
                    : '리셀러 허용 반영 실패'
                  : 'BQ 싱크 실패'}
          </div>
          <div style={{ font: '500 13px/1.5 "Pretendard"', color: 'var(--sc-text-muted)', marginBottom: 12 }}>
            {r.bq_sync_error
              ?? (r.bq_sync_status === 'pending'
                ? '승인은 기록됐지만 반영이 끝나지 못했습니다. 재시도해 주세요.'
                : '재시도가 필요합니다')}
          </div>
          <Btn variant="primary" onClick={retry} disabled={busy}>
            {r.request_type === 'price_apply'
              ? '단가 반영 재시도'
              : r.request_type === 'network_meta_apply'
                ? '메타데이터 반영 재시도'
                : resellerAllowed
                  ? '리셀러 허용 반영 재시도'
                  : '싱크 재시도'}
          </Btn>
        </div>
      )}

      {/* GAM 매핑 동기화 실패 — 메타데이터는 반영됐으니 동기화만 다시 돌린다(관리자). */}
      {isAdmin && !resellerAllowed && (gamSyncFailed || gamSyncStale) && (
        <div className={styles.card} style={{ borderColor: '#FFD8D2', background: '#FFF6F4' }}>
          <div className={styles.section} style={{ color: '#A12D14' }}>
            {gamSyncStale ? 'GAM 매핑 동기화 결과 미확인' : 'GAM 매핑 동기화 실패'}
          </div>
          <div style={{ font: '500 13px/1.5 "Pretendard"', color: 'var(--sc-text-muted)', marginBottom: 12 }}>
            {gamSyncStale
              ? '실행이 중단됐을 수 있어요 — Dagster 에서 run 상태를 확인한 뒤 필요하면 재시도해 주세요.'
              : gamSyncEvent?.note ?? '구글 리셀러 대시보드에 아직 반영되지 않았어요. 재시도해 주세요.'}
          </div>
          <Btn variant="primary" onClick={retryGamSync} disabled={busy || gamSync.steps !== null}>
            GAM 매핑 재시도
          </Btn>
        </div>
      )}

      {/* 리셀러 허용 재생성 실패 — 정본은 반영됐으니 재생성만 이 요청에 묶어 다시 돌린다(관리자). */}
      {isAdmin && resellerAllowed && (gamSyncFailed || gamSyncStale) && (
        <div className={styles.card} style={{ borderColor: '#FFD8D2', background: '#FFF6F4' }}>
          <div className={styles.section} style={{ color: '#A12D14' }}>
            {gamSyncStale ? '리셀러 허용 재생성 결과 미확인' : '리셀러 허용 재생성 실패'}
          </div>
          <div style={{ font: '500 13px/1.5 "Pretendard"', color: 'var(--sc-text-muted)', marginBottom: 12 }}>
            {gamSyncStale
              ? '실행이 중단됐을 수 있어요 — Dagster 에서 run 상태를 확인한 뒤 필요하면 재시도해 주세요.'
              : gamSyncEvent?.note ?? '정본은 반영됐지만 리셀러 대시보드에 아직 반영되지 않았어요. 재시도해 주세요.'}
          </div>
          <Btn variant="primary" onClick={retryGamSync} disabled={busy || gamSync.steps !== null}>
            재생성 재시도
          </Btn>
        </div>
      )}

      {/* 메타데이터 반영(동기 요청) 중에는 닫지 못하게 한다 — 닫은 뒤 실패하면 알릴 곳이 없다.
          GAM 동기화 단계는 서버에서 계속 돌기 때문에 닫아도 된다. */}
      <ProcessStepper
        steps={gamSync.steps}
        title={gamSync.title}
        onClose={gamSync.close}
        closableWhileActive={!gamSync.steps?.some((step) => step.key === 'meta' && step.status === 'active')}
      />

      {/* 승인 진행 과정 스텝퍼 — 직접 부여와 동일한 후속처리(BQ ACL · redash · partners) 표시 */}
      <ProcessStepper steps={processSteps} title={processTitle} onClose={closeStepper} />
    </div>
  );
}

interface PayloadProps {
  record: RequestRecord;
}

function RequestPayload({ record }: PayloadProps) {
  const r = record;
  const meta = useMetaContext();
  // 게임 id/prefix → 게임명 맵 — 요청 상세 games를 이름과 함께 표시.
  const gamesById = useMemo(() => buildGamesById(meta.games), [meta.games]);
  if (r.request_type === 'grant' || r.request_type === 'revoke') {
    return (
      <div>
        <div className={styles.kvGrid}>
          <KV label="target_email" value={r.target_email ?? '—'} />
          <KV label="level" value={r.level ? <RoleChip role={r.level as Level} /> : '—'} />
          <KV label="scope_type" value={r.scope_type ?? '—'} />
          <KV
            label="games"
            value={
              r.scope_type === 'all'
                ? '모든 게임'
                : r.game_prefixes.map((g) => gameLabel(g, gamesById)).join(', ') || '—'
            }
          />
        </div>
      </div>
    );
  }
  if (r.request_type === 'stage_grant') {
    const changes = stageGrantChanges(r.payload);
    return (
      <div className={styles.kvGrid}>
        <KV label="target_email" value={r.target_email ?? '—'} />
        {changes.map((c) => (
          <KV
            key={c.stage}
            label={stageName(c.stage)}
            value={c.level ? <RoleChip role={c.level as Level} /> : '권한 회수'}
          />
        ))}
      </div>
    );
  }
  if (r.request_type === 'role_member') {
    return (
      <div className={styles.kvGrid}>
        <KV label="계정" value={r.requester_email ?? '—'} />
        <KV label="요청 역할" value="member (승인 후 사용자 관리에서 leader 로 올릴 수 있어요)" />
        <KV label="생성 경로" value="Orbit 첫 로그인 시 자동 생성" />
      </div>
    );
  }
  if (r.request_type === 'api_token') {
    const payload = r.payload as Record<string, unknown>;
    const days = payload?.expires_in_days;
    return (
      <div className={styles.kvGrid}>
        <KV label="토큰 이름" value={(payload?.name as string) ?? '—'} />
        <KV label="유효 기간" value={days == null ? '무기한' : `${days}일`} />
        <KV label="신청자" value={r.requester_email ?? '—'} />
      </div>
    );
  }
  if (r.request_type === 'project_create' || r.request_type === 'project_edit') {
    const payload = r.payload as Record<string, unknown>;
    const entries = Object.entries(payload);
    if (entries.length === 0) {
      return <div style={{ color: 'var(--sc-text-muted)' }}>payload가 비어 있어요</div>;
    }
    return (
      <div className={styles.kvGrid}>
        {entries.map(([k, v]) => (
          <KV key={k} label={k} value={renderValue(v)} />
        ))}
      </div>
    );
  }
  if (r.request_type === 'price_apply') {
    return <PriceApplyPayload operations={priceOperations(r)} />;
  }
  if (r.request_type === 'network_meta_apply') {
    return <NetworkMetaPayload payload={r.payload as Record<string, unknown>} />;
  }
  if (r.request_type === 'reseller_allowed_apply') {
    return <ResellerAllowedPayload payload={r.payload as unknown as ResellerAllowedRequestPayload} />;
  }
  return null;
}

// 리셀러 허용 반영 요청 — 이메일 1건의 before(서버가 접수 때 읽은 행)와 after(반영할 값)를 비교한다.
// 추가는 after 만, 삭제는 before 만 있다. 리셀러 이름은 목록째 비교하면 '12개 → 13개'가 되어
// 무엇이 달라졌는지 안 보이므로, 추가·제거된 이름을 따로 강조한다.
type ResellerAllowedValues = Pick<ResellerAllowedRow, 'full_access' | 'reseller_names' | 'note'>;

function resellerScopeText(values: ResellerAllowedValues): string {
  if (values.full_access) return '전체 허용';
  return values.reseller_names.length === 0 ? '없음' : values.reseller_names.join(', ');
}

function ResellerAllowedValuesView({ values }: { values: ResellerAllowedValues }) {
  return (
    <div className={styles.metaFields}>
      <div className={styles.metaField}>
        <div className={styles.kvLabel}>보는 범위</div>
        <div className={styles.kvValue}>
          {values.full_access ? (
            '전체 허용 — GAM 매핑의 리셀러 전체'
          ) : values.reseller_names.length === 0 ? (
            '—'
          ) : (
            <div className={styles.metaItems}>
              {values.reseller_names.map((name) => (
                <span key={name} className={styles.metaItem}>
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.metaField}>
        <div className={styles.kvLabel}>메모</div>
        <div className={styles.kvValue}>
          <div className={styles.metaProse}>{values.note || '—'}</div>
        </div>
      </div>
    </div>
  );
}

function resellerAllowedChanges(before: ResellerAllowedValues, after: ResellerAllowedValues): MetaChange[] {
  const out: MetaChange[] = [];
  if (before.full_access !== after.full_access) {
    out.push({
      scope: '범위',
      label: '전체 허용',
      before: before.full_access ? 'ON' : 'OFF',
      after: after.full_access ? 'ON' : 'OFF',
    });
  }
  // 전체 허용이 켜지거나 꺼지면 이름 목록 자체가 의미를 바꾼다 — 그때는 범위 전체를 한 줄로 보인다.
  if (before.full_access || after.full_access) {
    if (before.full_access !== after.full_access) {
      out.push({ scope: '범위', label: '리셀러', before: resellerScopeText(before), after: resellerScopeText(after) });
    }
  } else {
    const was = new Set(before.reseller_names);
    const now = new Set(after.reseller_names);
    const added = after.reseller_names.filter((name) => !was.has(name));
    const removed = before.reseller_names.filter((name) => !now.has(name));
    if (added.length > 0) out.push({ scope: '리셀러', label: `추가 ${added.length}개`, before: '없음', after: added.join(', ') });
    if (removed.length > 0) out.push({ scope: '리셀러', label: `제거 ${removed.length}개`, before: removed.join(', '), after: '없음' });
  }
  if ((before.note ?? '') !== (after.note ?? '')) {
    out.push({ scope: '메모', label: '메모', before: before.note || '—', after: after.note || '—' });
  }
  return out;
}

function ResellerAllowedPayload({ payload }: { payload: ResellerAllowedRequestPayload }) {
  const { kind, before, after } = payload;
  const changes = kind === 'update' && before && after ? resellerAllowedChanges(before, after) : null;
  return (
    <div className={styles.metaPayload}>
      <div className={styles.kvGrid}>
        <KV
          label="대상 이메일"
          value={kind === 'bulk_create' ? `${payload.reseller_emails.length}개` : payload.reseller_email ?? '—'}
        />
        <KV label="변경 종류" value={resellerAllowedKindLabel(kind)} />
        {before && (
          <KV
            label="접수 시점 최종 수정"
            value={`${formatTimestamp(before.updated_at)}${before.updated_by ? ` · ${before.updated_by}` : ''}`}
          />
        )}
      </div>
      {kind === 'create' && after && <ResellerAllowedValuesView values={after} />}
      {/* 여러 이메일 추가 — 모두 같은 보는 범위·메모로 만든다. 승인 때 하나라도 이미 있으면 하나도 넣지 않는다. */}
      {kind === 'bulk_create' && after && (
        <>
          <div className={styles.metaFields}>
            <div className={styles.metaField}>
              <div className={styles.kvLabel}>추가할 이메일 {payload.reseller_emails.length}개</div>
              <div className={styles.kvValue}>
                <div className={styles.metaItems}>
                  {payload.reseller_emails.map((email) => (
                    <span key={email} className={styles.metaItem}>
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.metaNote}>
            아래 범위·메모가 모든 이메일에 똑같이 들어갑니다. 승인 때 그중 하나라도 이미 등록돼 있으면
            <b> 하나도 넣지 않고</b> 반영 실패로 남습니다(승인된 요청이라 반려할 수 없어요) — 그 이메일을
            정리한 뒤 반영을 재시도하거나, 그 이메일을 뺀 새 요청을 보내세요.
          </div>
          <ResellerAllowedValuesView values={after} />
        </>
      )}
      {kind === 'delete' && before && (
        <>
          <div className={styles.metaNote}>
            승인하면 이 이메일 행을 <b>지웁니다</b> — 다음 재생성부터 리셀러 대시보드에서 아무 리셀러도
            보지 못합니다. 아래는 지금 이 계정이 보는 범위입니다.
          </div>
          <ResellerAllowedValuesView values={before} />
        </>
      )}
      {changes !== null && (changes.length === 0 ? (
        <div className={styles.metaNote}>
          접수 시점의 값과 <b>달라지는 것이 없습니다</b>. 빈 요청입니다.
        </div>
      ) : (
        <div className={styles.metaDiffs}>
          {changes.map((change, index) => (
            <DiffRow key={`${change.scope}-${change.label}-${index}`} {...change} />
          ))}
        </div>
      ))}
    </div>
  );
}

// 네트워크 메타데이터 반영 요청 — 승인자가 봐야 하는 것은 '무엇을 저장하는가'가 아니라
// '무엇이 바뀌는가'다. 요청 payload 는 body(고칠 값)와 before(접수 시점의 행)를 함께
// 들고 있고, 여기서 둘을 비교해 달라진 것만 그린다. 단가 변경 요청의 expected/change 와
// 같은 방식이다 — 한 줄만 고친 요청에 미디에이션 9개가 전부 실려도 승인자는 그 한 줄만 본다.
//
// before 는 2026-08-28 부터 담긴다. 그 전에 접수된 요청은 비교 대상이 없어 보낸 값을
// 그대로 보여 준다(그 사실을 화면에 적는다).
const NETWORK_META_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'bi_network_name', label: 'BI 네트워크 이름' },
  { key: 'gam_network_code', label: 'GAM 코드' },
  { key: 'gam_placement_prefixes', label: 'GAM Placement ID 식별 규칙' },
  { key: 'refresh_policy_monthly', label: '월간 리프레시' },
  { key: 'refresh_policy_quarterly', label: '분기 리프레시' },
  { key: 'threshold_pct', label: '허용 편차' },
  { key: 'refresh_note', label: '특이사항' },
  // 없어진 기능(2026-08-28)이지만 대기 중인 옛 요청이 남아 있을 수 있다 —
  // 승인자가 무엇을 반려하는지 읽을 수 있어야 해서 라벨을 남긴다.
  { key: 'admob_network', label: 'AdMob ad source (없어진 기능)' },
];

// 미디에이션 항목 한 줄 — 어느 이름인지가 한눈에 읽혀야 한다. 플랫폼은 묶음 제목이
// 대신 말하므로 줄마다 반복하지 않는다.
// locale 은 en-US 가 아닐 때만 붙인다(대부분 en-US 라 매번 붙이면 잡음이 된다).
function mediationEntryText(item: Record<string, unknown>): string {
  const name = (item.api_name as string) ?? (item.dash_name as string) ?? '(이름 없음)';
  const dash = item.dash_name && item.api_name ? ` / ${item.dash_name as string}` : '';
  const custom = item.custom_network ? ` · ${item.custom_network as string}` : '';
  const locale = item.locale && item.locale !== 'en-US' ? ` (${item.locale as string})` : '';
  // network_id 는 표에서 빠졌지만 옛 요청 payload 에는 남아 있다.
  const id = item.network_id ? `${item.network_id as string} — ` : '';
  return `${id}${name}${dash}${custom}${locale}`;
}

// 값 하나를 사람 말로. 저장된 원문(compare_max)을 그대로 보이면 승인자가 화면에서 보던
// 이름과 달라 같은 값인지 매번 따져야 한다.
function networkMetaText(field: string, value: unknown): string {
  if (field === 'refresh_policy_monthly' || field === 'refresh_policy_quarterly') {
    return POLICY_LABEL[policyKey((value as RefreshPolicy | null) ?? null)];
  }
  if (field === 'threshold_pct') {
    return value === null || value === undefined ? '—' : `${String(value)}%p`;
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '없음' : value.map((item) => String(item)).join(', ');
  }
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

interface MetaChange {
  scope: string;
  label: string;
  before: string;
  after: string;
}

// 항목의 값 서명 — 전 필드가 같아야 같은 항목이다(변경 없음으로 걷어내는 용도로만 쓴다).
function entrySignature(item: Record<string, unknown>): string {
  return JSON.stringify([
    item.mediation ?? '',
    item.locale ?? '',
    item.api_name ?? '',
    item.dash_name ?? '',
    item.custom_network ?? '',
  ]);
}

const ENTRY_DIFF_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'locale', label: 'locale' },
  { key: 'api_name', label: 'API 이름' },
  { key: 'dash_name', label: '대시보드 이름' },
  { key: 'custom_network', label: '커스텀 네트워크' },
];

// 미디에이션 배열의 변경을 항목 단위로 편다. 배열째 비교하면 '9개 → 9개'가 되어
// 무엇이 달라졌는지 안 보인다.
//
// payload 를 오간 항목에는 편집 화면의 행 번호(rid)가 없다 — 저장 경계에서 떼기 때문이다.
// 그래서 값이 완전히 같은 항목을 먼저 짝지어 치우고, 남은 항목을 미디에이션 안에서
// 순서대로 짝지어 필드 diff 를 낸다. 값으로 동일성 키를 만들면 고친 필드가 곧 '다른
// 항목' 신호가 되어, 한 항목의 수정이 '제거 + 추가' 두 줄로 갈라진다(편집 화면에서
// 행 React key 를 값으로 만들었을 때와 같은 함정이다).
function mediationChanges(
  before: Array<Record<string, unknown>>,
  after: Array<Record<string, unknown>>,
): MetaChange[] {
  // 1) 값이 완전히 같은 항목은 변경이 아니다 — 개수까지 맞춰 걷어낸다(중복 항목 대비).
  const count = (items: Array<Record<string, unknown>>) => {
    const map = new Map<string, number>();
    for (const item of items) {
      const sig = entrySignature(item);
      map.set(sig, (map.get(sig) ?? 0) + 1);
    }
    return map;
  };
  const takeLeftovers = (
    items: Array<Record<string, unknown>>,
    others: Map<string, number>,
  ) => items.filter((item) => {
    const sig = entrySignature(item);
    const remaining = others.get(sig) ?? 0;
    if (remaining > 0) {
      others.set(sig, remaining - 1);
      return false;
    }
    return true;
  });
  const beforeLeft = takeLeftovers(before, count(after));
  const afterLeft = takeLeftovers(after, count(before));

  // 2) 남은 항목을 미디에이션별로 모아 순서대로 짝짓는다. 편집 화면이 행을 제자리에서
  //    고치므로, 같은 미디에이션 안에서 남은 항목의 순번이 곧 같은 행이다.
  const byMediation = (items: Array<Record<string, unknown>>) => {
    const map = new Map<string, Array<Record<string, unknown>>>();
    for (const item of items) {
      const key = (item.mediation as string) || '미디에이션';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return map;
  };
  const beforeGroups = byMediation(beforeLeft);
  const afterGroups = byMediation(afterLeft);
  const mediations = [...new Set([...beforeGroups.keys(), ...afterGroups.keys()])];

  const out: MetaChange[] = [];
  for (const mediation of mediations) {
    const olds = beforeGroups.get(mediation) ?? [];
    const news = afterGroups.get(mediation) ?? [];
    const pairs = Math.min(olds.length, news.length);
    for (let i = 0; i < pairs; i += 1) {
      for (const { key, label } of ENTRY_DIFF_FIELDS) {
        const was = String(olds[i][key] ?? '');
        const now = String(news[i][key] ?? '');
        if (was === now) continue;
        out.push({
          scope: mediation,
          label: `${mediationEntryText(olds[i])} · ${label}`,
          before: was || '없음',
          after: now || '없음',
        });
      }
    }
    for (const item of olds.slice(pairs)) {
      out.push({ scope: mediation, label: '제거', before: mediationEntryText(item), after: '없음' });
    }
    for (const item of news.slice(pairs)) {
      out.push({ scope: mediation, label: '추가', before: '없음', after: mediationEntryText(item) });
    }
  }
  return out;
}

function networkMetaChanges(
  body: Record<string, unknown>,
  before: Record<string, unknown>,
): MetaChange[] {
  const out: MetaChange[] = [];
  for (const { key, label } of NETWORK_META_FIELDS) {
    if (!(key in body)) continue;
    if (key === 'gam_network_code' && Array.isArray(body.gam_placement_prefixes)) continue;
    if (key === 'gam_placement_prefixes' && body[key] == null) continue;
    const previous = key === 'gam_placement_prefixes' && !(Array.isArray(before[key]) && before[key].length)
      ? before.gam_network_code : before[key];
    const was = networkMetaText(key, previous);
    const now = networkMetaText(key, body[key]);
    if (was === now) continue;
    out.push({ scope: '공통', label, before: was, after: now });
  }
  if (Array.isArray(body.mediation_info)) {
    out.push(
      ...mediationChanges(
        Array.isArray(before.mediation_info)
          ? (before.mediation_info as Array<Record<string, unknown>>)
          : [],
        body.mediation_info as Array<Record<string, unknown>>,
      ),
    );
  }
  return out;
}

// 변경점 한 줄 — '이전 → 이후'. 네트워크 메타(스코프 있음)와 단가(스코프 없음) 요청이
// 같은 모양을 공유한다. scope 가 없으면 첫 칸을 접은 4열 그리드로 그린다.
function DiffRow({
  scope,
  label,
  before,
  after,
}: {
  scope?: string;
  label: string;
  before: string;
  after: string;
}) {
  const className = scope === undefined
    ? `${styles.metaDiff} ${styles.metaDiffNoScope}`
    : styles.metaDiff;
  return (
    <div className={className}>
      {scope !== undefined && <span className={styles.metaDiffScope}>{scope}</span>}
      <span className={styles.metaDiffLabel}>{label}</span>
      <del className={styles.metaDiffBefore}>{before}</del>
      <b aria-hidden>→</b>
      <ins className={styles.metaDiffAfter}>{after}</ins>
    </div>
  );
}

// 미디에이션별로 묶어 보여 준다. 한 배열이라도 담당자는 플랫폼 단위로 읽는다 —
// 9개를 한 줄로 늘어놓으면 어느 플랫폼 몫이 몇 개인지가 안 보인다.
function MediationGroups({ items }: { items: Array<Record<string, unknown>> }) {
  if (items.length === 0) return <>—</>;
  const order: string[] = [];
  const groups = new Map<string, Array<Record<string, unknown>>>();
  for (const item of items) {
    const key = (item.mediation as string) || '(미지정)';
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(item);
  }
  return (
    <div className={styles.metaGroups}>
      {order.map((mediation) => (
        <div key={mediation} className={styles.metaGroup}>
          <div className={styles.metaGroupHead}>
            {mediation}
            <span className={styles.metaGroupCount}>{groups.get(mediation)!.length}</span>
          </div>
          <div className={styles.metaItems}>
            {groups.get(mediation)!.map((item, index) => (
              <span key={`${item.api_name ?? item.dash_name}-${index}`} className={styles.metaItem}>
                {mediationEntryText(item)}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NetworkMetaValue({ field, value }: { field: string; value: unknown }) {
  // 미디에이션 항목은 STRUCT 라 그대로 펴면 [object Object] 가 된다.
  // admob_network 는 없어진 AdMob 승인 요청이 보내던 같은 모양의 배열이다(옛 요청 전용).
  if ((field === 'mediation_info' || field === 'admob_network') && Array.isArray(value)) {
    return <MediationGroups items={value as Array<Record<string, unknown>>} />;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <>—</>;
    return (
      <div className={styles.metaItems}>
        {value.map((item, index) => (
          <span key={`${String(item)}-${index}`} className={styles.metaItem}>
            {String(item)}
          </span>
        ))}
      </div>
    );
  }
  if (field === 'refresh_note') {
    // 특이사항은 문장이라 등폭 글꼴·한 줄 표시가 맞지 않는다.
    return <div className={styles.metaProse}>{networkMetaText(field, value)}</div>;
  }
  return <>{networkMetaText(field, value)}</>;
}

// 보낸 값 전체 — 새 네트워크이거나 before 가 없는 옛 요청에서 쓴다.
function NetworkMetaFullBody({ body }: { body: Record<string, unknown> }) {
  const hidden = new Set(['expected_updated_at', 'sc_naming', 'reason']);
  const fields = [...NETWORK_META_FIELDS, { key: 'mediation_info', label: '미디에이션 이름' }];
  const known = fields.filter(({ key }) => key in body
    && !(key === 'gam_network_code' && Array.isArray(body.gam_placement_prefixes))
    && !(key === 'gam_placement_prefixes' && body[key] == null));
  // 스키마가 바뀌어 라벨이 없는 키가 남아도 값이 사라지면 안 된다 — 뒤에 원시 키로 붙인다.
  const unknown = Object.keys(body)
    .filter((key) => !hidden.has(key) && !fields.some((f) => f.key === key))
    .map((key) => ({ key, label: key }));
  return (
    <div className={styles.metaFields}>
      {[...known, ...unknown].map(({ key, label }) => (
        <div key={key} className={styles.metaField}>
          <div className={styles.kvLabel}>{label}</div>
          <div className={styles.kvValue}>
            <NetworkMetaValue field={key} value={body[key]} />
          </div>
        </div>
      ))}
    </div>
  );
}

// 그룹 판정 요청 — before.rows(그룹 전 행)와 body 를 비교해 4필드의 변경만 낸다.
// 행마다 옛 값이 갈라져 있으면(있으면 안 되지만 SQL 로 생길 수 있다) 값들을 나열한다 —
// 승인이 곧 통일이므로 승인자가 무엇들이 뭉개지는지 봐야 한다.
function GroupPolicyChanges({
  body,
  rows,
}: {
  body: Record<string, unknown>;
  rows: Array<Record<string, unknown>>;
}) {
  const fields: Array<{ key: string; label: string }> = [
    { key: 'refresh_policy_monthly', label: '월간 리프레시' },
    { key: 'refresh_policy_quarterly', label: '분기 리프레시' },
    { key: 'threshold_pct', label: '허용 편차' },
    { key: 'refresh_note', label: '특이사항' },
  ];
  const changes: MetaChange[] = [];
  for (const { key, label } of fields) {
    const olds = [...new Set(rows.map((row) => networkMetaText(key, row[key])))];
    const now = networkMetaText(key, body[key]);
    if (olds.length === 1 && olds[0] === now) continue;
    changes.push({ scope: '판정', label, before: olds.join(' / '), after: now });
  }
  if (changes.length === 0) {
    return (
      <div className={styles.metaNote}>
        접수 시점의 값과 <b>달라지는 것이 없습니다</b>. 그 사이 같은 내용이 이미 반영됐거나
        빈 요청입니다.
      </div>
    );
  }
  return (
    <div className={styles.metaDiffs}>
      {changes.map((change, index) => (
        <DiffRow key={`${change.label}-${index}`} {...change} />
      ))}
    </div>
  );
}

function NetworkMetaPayload({ payload }: { payload: Record<string, unknown> }) {
  const body = (payload?.body ?? {}) as Record<string, unknown>;
  const before = payload?.before as Record<string, unknown> | undefined;
  const kind = payload?.kind as string | undefined;
  const scNaming = (payload?.sc_naming as string) ?? '—';
  // 그룹 판정 요청 — 대상이 행이 아니라 그룹이다. 반영 범위(구성원)를 먼저 보인다.
  if (kind === 'policy') {
    const rows = ((before?.rows ?? []) as Array<Record<string, unknown>>);
    return (
      <div className={styles.metaPayload}>
        <div className={styles.kvGrid}>
          <KV label="그룹" value={(payload?.group as string) ?? scNaming} />
          <KV label="변경 종류" value={networkMetaKindLabel(kind)} />
        </div>
        <div className={styles.metaField}>
          <div className={styles.kvLabel}>반영 대상 {rows.length}개</div>
          <div className={styles.kvValue}>
            <div className={styles.metaItems}>
              {rows.map((row) => (
                <span key={String(row.sc_naming)} className={styles.metaItem}>
                  {String(row.sc_naming)}
                </span>
              ))}
            </div>
          </div>
        </div>
        <GroupPolicyChanges body={body} rows={rows} />
      </div>
    );
  }
  const changes = before ? networkMetaChanges(body, before) : null;
  return (
    <div className={styles.metaPayload}>
      <div className={styles.kvGrid}>
        <KV label="네트워크" value={scNaming} />
        <KV label="변경 종류" value={networkMetaKindLabel(kind)} />
      </div>
      {changes === null ? (
        <>
          {kind !== 'create' && (
            <div className={styles.metaNote}>
              접수 시점의 값이 없는 요청이라 <b>바뀌는 것만 가려낼 수 없습니다</b>. 아래는
              이 요청이 저장할 값 전체입니다.
            </div>
          )}
          <NetworkMetaFullBody body={body} />
        </>
      ) : changes.length === 0 ? (
        <div className={styles.metaNote}>
          접수 시점의 값과 <b>달라지는 것이 없습니다</b>. 그 사이 같은 내용이 이미 반영됐거나
          빈 요청입니다.
        </div>
      ) : (
        <div className={styles.metaDiffs}>
          {changes.map((change, index) => (
            <DiffRow key={`${change.scope}-${change.label}-${index}`} {...change} />
          ))}
        </div>
      )}
    </div>
  );
}

function displayRequestTypeLabel(requestType: RequestType): string {
  return requestType === 'price_apply' ? 'AF CPE 단가 반영 요청' : requestTypeLabel(requestType);
}

const PRICE_DIFF_FIELDS: Array<{ key: keyof AfPriceChangeInput; label: string }> = [
  { key: 'media_source', label: '미디어소스' },
  { key: 'campaign_name', label: '캠페인' },
  { key: 'event_name', label: '이벤트' },
  { key: 'country', label: '국가' },
  { key: 'unit_price', label: '단가 (입력 / BI 반영)' },
  { key: 'vat_included', label: '부가세' },
  { key: 'start_date', label: '적용 시작일' },
  { key: 'end_date', label: '적용 마지막일' },
];

/** 단가는 통화와 함께, 부가세는 말로 풀어 승인자가 바로 읽게 한다. */
function priceFieldValue(
  key: keyof AfPriceChangeInput,
  values: { unit_price: string; currency?: AfPriceCurrency; vat_included?: boolean },
): string {
  // 컬럼 추가 이전에 접수된 요청은 통화·부가세가 없다 — USD·포함으로 읽는다.
  if (key === 'unit_price') {
    return formatInputAndApplied(values.unit_price, values.currency ?? 'USD', values.vat_included ?? true);
  }
  if (key === 'vat_included') return formatVatIncluded(values.vat_included ?? true);
  return priceValue((values as Record<string, unknown>)[key]);
}

function priceOperations(r: RequestRecord): AfPriceBatchOperation[] {
  return Array.isArray(r.payload?.operations)
    ? r.payload.operations as AfPriceBatchOperation[]
    : [];
}

function PriceApplyPayload({ operations }: { operations: AfPriceBatchOperation[] }) {
  const byAction = (action: ReturnType<typeof priceOperationAction>) => (
    operations.filter((operation) => priceOperationAction(operation) === action)
  );
  const deletions = byAction('delete');
  const groups = [
    { title: '신규 추가', items: byAction('insert') },
    { title: '수정', items: byAction('update') },
    { title: '종료', items: byAction('close') },
    ...(deletions.length > 0 ? [{ title: '삭제(off)', items: deletions }] : []),
  ];
  return (
    <div style={{ display: 'grid', gap: 'var(--sc-space-4)' }}>
      {groups.map((group) => (
        <section
          key={group.title}
          style={{ border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-sm)', overflow: 'hidden' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 14px', background: 'var(--sc-bg-muted)' }}>
            <b>{group.title}</b><span>{group.items.length}건</span>
          </div>
          {group.items.length === 0 ? (
            <div style={{ padding: 14, color: 'var(--sc-text-muted)' }}>변경 없음</div>
          ) : group.items.map((operation) => (
            <div key={operation.op_id} style={{ padding: 14, borderTop: '1px solid var(--sc-border)' }}>
              <div style={{ marginBottom: 10, fontWeight: 700 }}>
                {operation.change.media_source} · {operation.change.campaign_name || '전체 캠페인'} · {operation.change.billing_basis === 'install' ? '설치수 × 단가' : operation.change.event_name}
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {priceOperationAction(operation) === 'delete' ? (
                  <DiffRow
                    label="상태"
                    before="on"
                    after={`삭제(off) · ${operation.change.country || '전체 국가'} · ${priceFieldValue('unit_price', operation.change)} · ${priceFieldValue('start_date', operation.change)} ~ ${priceFieldValue('end_date', operation.change)}`}
                  />
                ) : operation.type === 'insert' ? (
                  PRICE_DIFF_FIELDS.map(({ key, label }) => (
                    <DiffRow key={key} label={label} before="—" after={priceFieldValue(key, operation.change)} />
                  ))
                ) : (
                  PRICE_DIFF_FIELDS.flatMap(({ key, label }) => {
                    const before = key in operation.expected || key === 'vat_included'
                      ? priceFieldValue(key, operation.expected)
                      : '—';
                    const after = priceFieldValue(key, operation.change);
                    return before === after ? [] : [
                      <DiffRow key={key} label={label} before={before} after={after} />,
                    ];
                  })
                )}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function priceValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '미정';
  return String(value);
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className={styles.kvLabel}>{label}</div>
      <div className={styles.kvValue}>{value}</div>
    </div>
  );
}
